import React, { useMemo } from 'react';
import { projectMiniMapLayout, formatRackCount, MiniMapRackInput } from '../../utils/miniMapProjection.logic';

export interface DcMiniMapProps {
  racksList: MiniMapRackInput[];
  currentRackId: string | number;
  onSelectRack?: (rackId: string) => void;
  className?: string;
}

export const DcMiniMap: React.FC<DcMiniMapProps> = ({
  racksList,
  currentRackId,
  onSelectRack,
  className = '',
}) => {
  if (!racksList || racksList.length === 0) return null;

  const VW = 220;
  const VH = 88;
  const PAD = 8;

  const projection = useMemo(() => {
    return projectMiniMapLayout(racksList, currentRackId, {
      width: VW,
      height: VH,
      padding: PAD,
    });
  }, [racksList, currentRackId]);

  const { hasRealCoords, racks } = projection;

  return (
    <div data-testid="dc-minimap" className={`flex flex-col gap-1 w-full ${className}`}>
      <div className="flex items-center justify-between text-[10px] text-slate-500 font-mono px-0.5">
        <span>План зала ({formatRackCount(racks.length)})</span>
        {!hasRealCoords && (
          <span className="text-amber-500/80 italic text-[9px]">по рядам</span>
        )}
      </div>

      <div className="relative rounded overflow-hidden border border-slate-800 bg-[#090d16] shadow-inner">
        <svg
          viewBox={`0 0 ${VW} ${VH}`}
          className="w-full h-auto block select-none"
          preserveAspectRatio="xMidYMid meet"
          role="region"
          aria-label="Миникарта расположения стойки в машинном зале"
        >
          <defs>
            {/* Subtle glow filter for active rack */}
            <filter id="minimap-glow" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="1.5" result="blur" />
              <feComposite in="SourceGraphic" in2="blur" operator="over" />
            </filter>
          </defs>

          {/* Hall boundary guide grid line */}
          <rect
            x={1}
            y={1}
            width={VW - 2}
            height={VH - 2}
            fill="none"
            stroke="#1e293b"
            strokeWidth={0.5}
            strokeDasharray="2 4"
            rx={2}
          />

          {/* Projected Racks */}
          {racks.map((r) => {
            const tooltip = onSelectRack ? r.tooltip : `${r.label} (ID: ${r.id})`;

            return (
              <g
                key={r.id}
                data-testid={`minimap-rack-${r.id}`}
                onClick={() => {
                  if (onSelectRack && !r.isCurrent) {
                    onSelectRack(r.id);
                  }
                }}
                className={`transition-all duration-150 ${
                  r.isCurrent
                    ? 'cursor-default'
                    : onSelectRack
                    ? 'cursor-pointer hover:opacity-100'
                    : 'cursor-default'
                }`}
                tabIndex={onSelectRack && !r.isCurrent ? 0 : -1}
                role={onSelectRack && !r.isCurrent ? 'button' : 'presentation'}
                aria-label={tooltip}
                onKeyDown={(e) => {
                  if (onSelectRack && !r.isCurrent && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    onSelectRack(r.id);
                  }
                }}
              >
                <title>{tooltip}</title>

                {/* Rack footprint rect */}
                <rect
                  x={r.x}
                  y={r.y}
                  width={r.width}
                  height={r.height}
                  rx={0.75}
                  fill={r.isCurrent ? '#0891b2' : '#1e293b'}
                  stroke={r.isCurrent ? '#67e8f9' : '#334155'}
                  strokeWidth={r.isCurrent ? 1.2 : 0.6}
                  filter={r.isCurrent ? 'url(#minimap-glow)' : undefined}
                />

                {/* Front-door accent indicator (shows service aisle direction) */}
                <line
                  data-testid={`minimap-door-${r.id}`}
                  x1={r.door.x1}
                  y1={r.door.y1}
                  x2={r.door.x2}
                  y2={r.door.y2}
                  stroke={r.isCurrent ? '#a5f3fc' : '#64748b'}
                  strokeWidth={r.isCurrent ? 1.5 : 0.8}
                  strokeLinecap="round"
                />

                {/* Active rack indicator marker */}
                {r.isCurrent && (
                  <circle
                    data-testid="minimap-active-marker"
                    cx={r.x + r.width / 2}
                    cy={r.y + r.height / 2}
                    r={Math.min(r.width, r.height) / 3.5}
                    fill="#ecfeff"
                  />
                )}
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
};
